"use client";

import React from "react";
import Image from "next/image";
import s from "../styles.parts";
import Consts from "@common/config/consts";
import { dayjs, logjs } from "@lib/client";
import { usePartsList } from "../usePartsList";

const log = new logjs("PartsLabelListArea");

// スタイル定数
const INPUT_STYLE = { width: "8em" };
const ICON_CONTAINER_STYLE = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
};

/**
 * 部品情報ラベル表示コンポーネント
 * partslabellist.ctpをReactに移植したラベル表示コンポーネント
 * 既存の回答データを読み取り専用で表示し、Hacchu_flgによる状態表示、
 * 在庫確認ボタン、他社成約表示などのロジックを含む
 *
 * @param {Object} props.qaDialogProps - 質問ダイアログのプロパティ
 * @param {Object} props.qaabDialogProps - 在庫確認（QAAB）ダイアログのプロパティ
 */
export default function PartsLabelListArea({ data, state, actions, quote_number, photoDialogProps, fileupDialogProps, qaDialogProps, qaabDialogProps }) {
  log.debug("START PartsLabelListArea");
  log.debug("---------------------  PartsLabelListArea data", data);

  const initState = {
    mitsumoriNo: quote_number,
    request: data?.request,
    answer: data?.answer,
    AqbData: data?.AqbData ?? {},
  };

  // 共通関数の取得
  const [form, formProps] = usePartsList(initState, state, actions);

  // getHacchuStatusWithNoneを使用（bgcolor: "none"版）
  const getHacchuStatus = formProps.getHacchuStatusWithNone;

  // データ取得
  const partsList = data?.partsList || [];
  const partskind = state?.initData?.partskind || []; //data?.partskind || [];
  const answer = data?.answer || [];
  const request = data?.request || {};
  const prSeiyaku = data?.prSeiyaku || {};
  const prMySeiyaku = data?.prMySeiyaku || {};
  const resPict = data?.resPict || {};
  const resFile = data?.resFile || {};
  const requestLimit = request?.OrderedRequest?.Request_Limit;
  const tr_class = data?.tr_class || "";

  // 部品をグループ化（PRIDまたはOthPartsNameでグループ化）
  // PHPテンプレート: for ($i = 0; $i < count($partsList); $i++):
  let groupIdx = 0; // 部品グループインデックス
  const groupedParts = [];

  for (let i = 0; i < partsList.length; i++) {
    const partsItem = partsList[i];
    const pl_prid = partsItem?.PRID; // partsItem?.PartsAnswer?.PRID
    const pl_paid = partsItem?.id; // partsItem?.PartsAnswer?.id

    // PHPテンプレート: if( $pl_prid == 0 && empty( $partsList[$i]['PartsAnswer']['StanderdPrice'] ) ){ continue; }
    if (pl_prid === 0 && !partsItem?.StanderdPrice) {
      // partsItem?.PartsAnswer?.StanderdPrice
      continue;
    }

    // グループの最初の部品かどうかを判定
    if (groupedParts.length === 0) {
      groupedParts.push({
        s: groupIdx,
        items: [{ partsItem, originalIndex: i }],
        pl_prid: pl_prid,
        pl_paid: pl_paid,
      });
    } else {
      const prevGroup = groupedParts[groupedParts.length - 1];
      const prevPlPrid = prevGroup.pl_prid;
      const prevOthPartsName = prevGroup.items[0]?.partsItem?.OthPartsName; // partsItem?.PartsAnswer?.OthPartsName

      // グループ化の判定
      const shouldStartNewGroup = (prevPlPrid === 0 && prevOthPartsName !== partsItem?.OthPartsName) || prevPlPrid !== pl_prid; // partsItem?.PartsAnswer?.OthPartsName

      if (shouldStartNewGroup) {
        groupIdx++;
        groupedParts.push({
          s: groupIdx,
          items: [{ partsItem, originalIndex: i }],
          pl_prid: pl_prid,
          pl_paid: pl_paid,
        });
      } else {
        prevGroup.items.push({ partsItem, originalIndex: i });
      }
    }
  }

  // 請求期限とCARRY_TAXIN_DATEの比較
  const isAfterCarryTaxinDate = formProps.isAfterCarryTaxinDate(requestLimit);

  // 質問ボタンの表示判定（Request_Limit >= 現在日時の場合のみ表示）
  const showQaButton = data?.request?.Request_Limit && (dayjs(data.request.Request_Limit).isAfter(dayjs()) || dayjs(data.request.Request_Limit).isSame(dayjs(), "day"));

  return (
    <>
      {/* ヘッダーセクション */}
      <s.PartsTitle>
        部品情報
        {showQaButton && data?.partsListReq?.[0]?.id && (
          <s.QuestionButton
            data-key={data.partsListReq[0].id}
            onClick={(e) => {
              e.preventDefault();
              qaDialogProps.open({
                MitsumoriNo: quote_number,
                isQuestion: state.user?.Gyoshu_flg === 1,
                request: data?.request,
                answer: data?.answer,
              });
            }}
          >
            依頼元へ質問を行う | 回答を見る
          </s.QuestionButton>
        )}
        {isAfterCarryTaxinDate && (
          <font style={{ color: "#0000ff", fontSize: "12pt" }}> ※上記の車両情報（グレード、カラ―等）につきましては、念の為、車台番号から再度ご確認のうえ、お見積りをお願い致します。</font>
        )}
      </s.PartsTitle>
      <div style={{ color: "#0000ff", fontSize: "12pt", fontWeight: "bolder" }}>※送料は「税別」にて記載してください。</div>

      {/* 最終回答者表示 */}
      {answer?.[0]?.UserName && (
        <s.FinalAnswererContainer>
          <s.FinalAnswererLabel>最終回答者： {answer[0].UserName || ""} 様</s.FinalAnswererLabel>
        </s.FinalAnswererContainer>
      )}

      {/* 部品リストテーブル */}
      <s.PLTable className="normal order_info">
        <tbody>
          {groupedParts.map((group) => {
            log.debug("group", group);
            const { s: groupIndex, items, pl_prid, pl_paid } = group;
            const firstPartsItem = items[0]?.partsItem || items[0];

            log.debug("firstPartsItem", firstPartsItem);

            // 他社成約かどうか
            let thCol = 13;
            let thColor = {};
            let showOtherCompanyContract = false;

            if (pl_prid > 0) {
              const hacchuflg = prMySeiyaku[pl_prid];
              if (prSeiyaku[pl_prid]) {
                // 成約しているので自分かどうか
                // PHPテンプレート: if($hacchuflg != 2 and $hacchuflg != 5 and $hacchuflg != 8 and $hacchuflg != 9)
                if (hacchuflg !== 2 && hacchuflg !== 5 && hacchuflg !== 8 && hacchuflg !== 9) {
                  thCol = 11;
                  thColor = { borderLeftStyle: "none", background: "#696969", color: "#FFF" };
                  showOtherCompanyContract = true;
                }
              }
            }

            // 部品名とカテゴリアイコンを生成
            let partsName = "";
            let categoryIcons = [];
            let pk3flg = 0;

            if (pl_prid === 0) {
              // その他部品
              partsName = firstPartsItem?.OthPartsName || "";
              if (firstPartsItem?.OthPartsSonota) {
                partsName += `　品名：${firstPartsItem.OthPartsSonota}`;
              }
            } else {
              // 通常の部品
              // PHPテンプレート: echo $partsList[$i]['PartsRequest']['P_PartsName']."&nbsp;";
              partsName = firstPartsItem?.P_PartsName || "";
              // 部品カテゴリアイコンを生成（PKID3は除く）
              partskind.forEach((category) => {
                const catePKID = category?.PartsKindID;
                const catePKName = category?.PartsKindName;
                const pkid1 = firstPartsItem?.P_PKID1;
                const pkid2 = firstPartsItem?.P_PKID2;
                const pkid3 = firstPartsItem?.P_PKID3;
                const pkid4 = firstPartsItem?.P_PKID4;
                const pkid5 = firstPartsItem?.P_PKID5;

                if (catePKID === pkid1 || catePKID === pkid2 || catePKID === pkid3 || catePKID === pkid4 || catePKID === pkid5) {
                  if (catePKID === 3) {
                    pk3flg = 1;
                    return; // PKID3は後で処理
                  }

                  const className = formProps.getCategoryClassName(catePKID);
                  if (className) {
                    categoryIcons.push(
                      <React.Fragment key={`icon-${catePKID}`}>
                        <span className={className}>{catePKName}</span>&nbsp;
                      </React.Fragment>
                    );
                  }
                }
              });

              // PKID3は最後に表示
              if (pk3flg === 1) {
                categoryIcons.push(
                  <React.Fragment key="icon-3-space">
                    　　<span className="icon_g">{formProps.getCategoryName(3, partskind)}</span>
                  </React.Fragment>
                );
              }

              // 部品備考
              if (firstPartsItem?.Bikou == null) {
                categoryIcons.push(
                  <React.Fragment key="bikou-empty">
                    <p></p>
                  </React.Fragment>
                );
              } else {
                categoryIcons.push(
                  <React.Fragment key="bikou">
                    <p style={{ fontWeight: "normal" }}>({firstPartsItem.Bikou})</p>
                  </React.Fragment>
                );
              }
            }

            // グループ内の部品をループ（items配列を使用）
            // PHPテンプレート: for (; $i < count($partsList); $i++):
            let j = 0;
            let display = 0;
            const answerRows = [];

            for (let itemIndex = 0; itemIndex < items.length; itemIndex++) {
              const { partsItem, originalIndex } = items[itemIndex];
              const i = originalIndex;

              log.debug("partsItem", partsItem);

              // グループ終了条件をチェック
              // PHPテンプレート: if( ($i + 1) >= count($partsList) || ... )
              const nextItemIndex = i + 1;
              const isGroupEnd =
                nextItemIndex >= partsList.length ||
                (pl_prid === 0 && partsItem?.OthPartsName !== partsList[nextItemIndex]?.OthPartsName) ||
                (pl_prid > 0 && pl_prid !== partsList[nextItemIndex]?.PRID);

              // PHPテンプレート: $paid = ($SessionData == null)? $partsList[$i]['PartsAnswer']['id'] : $SessionData['Answer'][$s][$j]['PAID'];
              const paid = partsItem?.id;

              if (partsItem?.StanderdPrice) {
                display = 1;

                // 在庫確認ボタンの表示
                const dataLimit = formProps.getLaterLimit(data?.request?.Request_Limit, partsItem?.Sell_Limit);
                const btNameQab = formProps.getQabButtonNameWithAqb(paid, dataLimit);
                log.debug(`btNameQab: ${btNameQab} paid: ${paid}`);

                // Hacchu_flgによる状態判定
                const hacchuFlg = partsItem?.Hacchu_flg;
                const status = getHacchuStatus(hacchuFlg);
                const { jyotai, bgcolor } = status;

                // 送料表示（返品の場合の特別処理）
                const soryo = partsItem?.Soryo ?? partsItem?.[0]?.Soryo ?? 0;
                const deliveryPriceDisplay = soryo === 0 && hacchuFlg !== Consts.HACCHU_FLG.HENPIN ? "送料込" : `${formProps.formatNumber(soryo)}円`;

                // 部品カテゴリ名を取得
                const partsKindName = formProps.getCategoryName(partsItem?.PartsKindID, partskind); // partsItem?.PartsAnswer?.PartsKindID

                // 写真・ファイル参照（YUKO_REF）
                const seqnum = partsItem?.Seqnum || 1;
                const hasPhoto = resPict[paid];
                const hasFile = resFile[paid];

                answerRows.push(
                  <tr key={`row-${i}-${j}`} className={bgcolor}>
                    <td>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                        {jyotai}
                        {jyotai && <br />}
                        {btNameQab && (
                          <Image
                            src={`/images/${btNameQab}.png`}
                            className="qabOpen btn_gray"
                            data-key={`${answer?.[0]?.RequestAnswer?.id}/${paid}`}
                            style={{ cursor: "pointer" }}
                            onClick={(e) => {
                              e.preventDefault();
                              qaabDialogProps.open({
                                mitsumoriNo: quote_number,
                                raid: partsItem?.RAID || null,
                                paid: partsItem?.id || null,
                                isQuestion: state.user?.Gyoshu_flg === 2,
                              });
                            }}
                            alt="在庫確認"
                            width={90}
                            height={24}
                          />
                        )}
                      </div>
                    </td>
                    <td scope="row">{partsItem?.PartsJyunseiCode || ""}</td>
                    <td>{partsItem?.Suryo || ""}</td>
                    <td>{partsKindName}</td>
                    <td>{formProps.formatNumber(partsItem?.PartsPrice ?? partsItem?.[0]?.PartsPrice ?? "")}円</td>
                    <td>{deliveryPriceDisplay}</td>
                    <td>{formProps.getHassoStatusText(partsItem?.Hasso)}</td>
                    <td>{formProps.formatDateShort(partsItem?.Sell_Limit) || ""}</td>
                    <td>{partsItem?.ColorNo || ""}</td>
                    <td>{partsItem?.ColorName || ""}</td>
                    <td>
                      {/* 写真・ファイル参照（YUKO_REF） */}
                      {(() => {
                        if (pl_prid === 0) {
                          // その他部品
                          return (
                            <div style={ICON_CONTAINER_STYLE}>
                              {hasPhoto && (
                                <a
                                  href={`/ordereddetail/photo/${quote_number}/0/${seqnum}/${Consts.WRECKER_PHOTO.YUKO_REF}`}
                                  rel="internal"
                                  onClick={formProps.handlePhotoClick(photoDialogProps, quote_number, 0, seqnum, Consts.WRECKER_PHOTO.YUKO_REF)}
                                >
                                  <Image src="/images/camera_icon.png" alt="写真" width={24} height={20} />
                                </a>
                              )}
                              {hasFile && <br />}
                              {hasFile && (
                                <a
                                  href={`/ordereddetail/fileup/${quote_number}/0/${seqnum}/${Consts.WRECKER_PHOTO.YUKO_REF}/${partsItem?.OthPartsName || ""}`}
                                  rel="internal"
                                  onClick={formProps.handleFileupClick(fileupDialogProps, quote_number, 0, seqnum, Consts.WRECKER_PHOTO.YUKO_REF, partsItem?.OthPartsName || "")}
                                  style={{ cursor: "pointer", padding: "5px 0px 0px" }}
                                >
                                  <Image src="/images/file_icon.png" alt="ファイル" width={20} height={24} />
                                </a>
                              )}
                            </div>
                          );
                        } else {
                          // 通常の部品
                          return (
                            <div style={ICON_CONTAINER_STYLE}>
                              {hasPhoto && (
                                <a
                                  href={`/ordereddetail/photo/${quote_number}/${partsItem?.id}/${seqnum}/${Consts.WRECKER_PHOTO.YUKO_REF}`}
                                  rel="internal"
                                  onClick={formProps.handlePhotoClick(photoDialogProps, quote_number, partsItem?.id, seqnum, Consts.WRECKER_PHOTO.YUKO_REF)}
                                >
                                  <Image src="/images/camera_icon.png" alt="写真" width={24} height={20} />
                                </a>
                              )}
                              {hasFile && (<br />)}
                              {hasFile && (
                                <a
                                  href={`/ordereddetail/fileup/${quote_number}/${partsItem?.id}/${seqnum}/${Consts.WRECKER_PHOTO.YUKO_REF}`}
                                  rel="internal"
                                  onClick={formProps.handleFileupClick(fileupDialogProps, quote_number, partsItem?.id, seqnum, Consts.WRECKER_PHOTO.YUKO_REF)}
                                  style={{ cursor: "pointer", padding: "5px 0px 0px" }}
                                >
                                  <Image src="/images/file_icon.png" alt="ファイル" width={20} height={24} />
                                </a>
                              )}
                            </div>
                          );
                        }
                      })()}
                    </td>
                    <td>{formProps.nl2br(partsItem?.Bikou || "")}</td>
                    <td scope="row" style={{ backgroundColor: "#FDF5E6" }}>
                      {partsItem?.JishaNo || ""}
                      <br />
                      {/* PrepKindの表示 */}
                      {partsItem?.PrepKind === Consts.GOODS.INHOUSE && <label>自社品</label>}
                      {partsItem?.PrepKind === Consts.GOODS.OTHER && <label>他社品</label>}
                    </td>
                  </tr>
                );

                j++;
              }

              // グループ終了条件をチェック
              if (isGroupEnd) {
                // 未回答の場合（display == 0 && pl_prid != 0）
                if (display === 0 && pl_prid !== 0) {
                  answerRows.push(
                    <tr key={`empty-${groupIndex}`} className="none">
                      <td>-</td>
                      <td>-</td>
                      <td>-</td>
                      <td>-</td>
                      <td>-</td>
                      <td>-</td>
                      <td>-</td>
                      <td>-</td>
                      <td>-</td>
                      <td>-</td>
                      <td>-</td>
                      <td>-</td>
                      <td>-</td>
                    </tr>
                  );
                }
                break;
              }
            }

            return (
              <React.Fragment key={`group-${groupIndex}`}>
                {/* 部品名ヘッダー行 */}
                <s.PLHeadTr1>
                  {/* 他社成約表示 */}
                  {showOtherCompanyContract && (
                    <s.PLHeadTh1
                      colSpan={2}
                      className={`title ${tr_class}`}
                      scope="row"
                      style={{ borderRightStyle: "none", background: "#696969", textAlign: "center", verticalAlign: "middle" }}
                    >
                      <Image src="/images/tasha.png" alt="他社成約" width={120} height={34} />
                    </s.PLHeadTh1>
                  )}
                  <s.PLHeadTh1 colSpan={thCol} className={`title ${tr_class}`} scope="row" style={thColor}>
                    {/* PHPテンプレート: 部品名 + &nbsp; + カテゴリアイコン */}
                    {pl_prid === 0 ? (
                      <>{partsName}</>
                    ) : (
                      <>
                        {partsName}&nbsp;{categoryIcons}
                      </>
                    )}
                  </s.PLHeadTh1>
                </s.PLHeadTr1>

                {/* ヘッダー行 */}
                <s.PLHeadTr2>
                  <s.PLHeadTh2>&nbsp;</s.PLHeadTh2>
                  <s.PLHeadTh2 scope="row">純正品番</s.PLHeadTh2>
                  <s.PLHeadTh2 style={{}}>数量</s.PLHeadTh2>
                  <s.PLHeadTh2 style={{}}>部品カテゴリ</s.PLHeadTh2>
                  <s.PLHeadTh2>価格(税別)</s.PLHeadTh2>
                  {isAfterCarryTaxinDate ? (
                    <s.PLHeadTh2 style={{ backgroundColor: "#FFF666" }}>送料(税別)</s.PLHeadTh2>
                  ) : (
                    <s.PLHeadTh2>
                      送料<span style={{ fontWeight: "bold", backgroundColor: "rgb(255, 255, 0)" }}>(税込)</span>
                    </s.PLHeadTh2>
                  )}
                  <s.PLHeadTh2>発送可能日</s.PLHeadTh2>
                  <s.PLHeadTh2>見積有効期限</s.PLHeadTh2>
                  <s.PLHeadTh2>カラー番号</s.PLHeadTh2>
                  <s.PLHeadTh2>カラー名称</s.PLHeadTh2>
                  <s.PLHeadTh2>写真</s.PLHeadTh2>
                  <s.PLHeadTh2>部品備考</s.PLHeadTh2>
                  <s.PLHeadTh2 scope="row">自社管理番号</s.PLHeadTh2>
                </s.PLHeadTr2>

                {/* 回答行 */}
                {answerRows}
              </React.Fragment>
            );
          })}
        </tbody>
      </s.PLTable>
    </>
  );
}
